/**
 * Store Zustand pour la session utilisateur
 *
 * Centralise toutes les donnees de session (user, company, modules,
 * permissions, features) en memoire. Pas de persist localStorage
 * pour les donnees de permissions.
 *
 * Le store est initialise une seule fois au mount de SessionProvider
 * via GET /auth/session, puis rafraichi uniquement sur evenements discrets.
 *
 * @module sessionStore
 * @author Equipe Brydge
 * @since 3.0.0
 */

import {create} from 'zustand';
import {mutate} from 'swr';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {setSelectedCompany, getSelectedCompanyId, clearSelectedCompany} from 'packages/common/utils/companyStorage';
import {isTransientFailure} from 'packages/common/utils/authFailure';
import {SESSION_STATUS} from 'packages/common/stores/sessionStatus';

const SESSION_ENDPOINT = '/auth/session';

// Délai maximal de la requête de session, posé ici et non sur l'instance Axios :
// les requêtes métier (export, génération de PDF) ont le droit d'être longues,
// celle-ci non. Sans borne, une connexion acceptée par un répartiteur qui n'a
// personne derrière laisse l'utilisateur sur un écran de chargement muet, sans
// limite de temps et sans rien à faire. Avec elle, l'attente devient un échec
// passager, donc l'écran de reconnexion et ses reprises automatiques.
const SESSION_REQUEST_TIMEOUT = 15 * 1000;

/**
 * Statuts du magasin de session.
 *
 * `unavailable` se distingue de `error` : le serveur n'a pas répondu, rien ne
 * dit que la session est en cause. L'établissement choisi est conservé et le
 * provider retente seul. `error` signale un refus dont l'utilisateur doit être
 * averti.
 *
 * `closed` est terminal : la session est finie et une page de connexion arrive.
 * Il se distingue d'`idle` parce que le provider réinitialise sur `idle` : sans
 * lui, la fin de session relancerait un chargement pendant que la navigation se
 * prépare, et l'écran d'erreur s'afficherait par-dessus.
 */
const INITIAL_STATE = {
  status: SESSION_STATUS.IDLE,
  error: null,
  user: null,
  account: null,
  activeCompany: null,
  companies: [],
  modules: {},
  userFeatures: [],
  userRole: null,
  adminAccess: null,
  isFullAccess: false,
  _lastRefresh: null,
  _switchSeq: 0,
  _initSeq: 0,
};

/**
 * Appelle le endpoint session et applique les donnees au store
 *
 * Fonction interne partagee entre initialize (avec loading) et
 * refreshSession (silencieux). Garantit un format d'etat identique
 * quel que soit le chemin d'appel.
 *
 * @param {Function} set - Setter Zustand
 * @param {string|null} companyId - ID de company optionnel
 * @return {Promise<Object>} Donnees de session recues du backend
 * @throws {Error} Si la reponse est invalide ou la requete echoue
 */
const fetchAndApplySession = async (set, companyId) => {
  const url = companyId ? `${SESSION_ENDPOINT}?companyId=${companyId}` : SESSION_ENDPOINT;
  const response = await axiosInstance.get(url, {timeout: SESSION_REQUEST_TIMEOUT});
  const data = response.data?.data;

  if (!data) {
    throw new Error('Reponse session invalide');
  }

  set({
    status: SESSION_STATUS.READY,
    error: null,
    user: data.user,
    account: data.account || null,
    activeCompany: data.activeCompany,
    companies: data.companies || [],
    modules: data.modules || {},
    userFeatures: data.userFeatures || [],
    userRole: data.userRole || null,
    adminAccess: data.adminAccess || null,
    isFullAccess: data.isFullAccess || false,
    _lastRefresh: Date.now(),
  });

  return data;
};

/**
 * Traduit un échec de chargement en état de magasin.
 *
 * Une panne laisse le magasin en attente de reprise, sans message alarmant et
 * sans toucher à l'établissement sélectionné : rien ne prouve qu'il soit en
 * cause. Un refus, lui, doit être montré à l'utilisateur.
 *
 * @param {Error} error - Erreur remontée par la requête de session
 * @return {Object} Fragment d'état à appliquer
 */
const describeFailure = (error) => {
  if (isTransientFailure(error)) {
    return {status: SESSION_STATUS.UNAVAILABLE, error: null};
  }
  return {status: SESSION_STATUS.ERROR, error: error.message || 'Erreur de chargement session'};
};

const useSessionStore = create((set, get) => ({
  ...INITIAL_STATE,

  /**
   * Charge les donnees de session depuis le backend
   *
   * Passe le store en 'loading' pendant le chargement, sauf s'il est déjà en
   * 'unavailable' : une tentative de reprise ne doit pas faire clignoter
   * l'écran de reconnexion. Synchronise ensuite le localStorage avec la company
   * retournée.
   *
   * N'échoue jamais par exception : l'issue est inscrite dans le statut, que
   * l'appelant doit lire (voir `switchCompany`).
   *
   * @param {string|null} companyId - ID de company optionnel
   */
  initialize: async (companyId = null) => {
    const seq = get()._initSeq + 1;

    // Une tentative de reprise ne repasse pas par l'écran de chargement :
    // l'utilisateur est déjà devant l'écran de reconnexion, et le faire
    // clignoter à chaque essai donnerait l'impression d'une application
    // instable au moment précis où il faut le rassurer.
    const reconnecting = get().status === SESSION_STATUS.UNAVAILABLE;
    set(reconnecting ? {_initSeq: seq} : {status: SESSION_STATUS.LOADING, _initSeq: seq});

    try {
      const data = await fetchAndApplySession(set, companyId);

      // Ignorer si un initialize plus recent a ete lance entre-temps
      if (get()._initSeq !== seq) return;

      // Synchroniser le localStorage avec la company retournee par le backend
      if (data.activeCompany?._id) {
        const currentLocal = getSelectedCompanyId();
        if (!currentLocal || currentLocal !== data.activeCompany._id) {
          setSelectedCompany(data.activeCompany._id, {
            name: data.activeCompany.name,
            slug: data.activeCompany.slug,
          });
        }
      }
    } catch (error) {
      // Ignorer si un initialize plus recent a ete lance entre-temps
      if (get()._initSeq !== seq) return;

      // Si le companyId du localStorage est invalide (403), vider et relancer
      // sans companyId pour laisser le backend choisir la bonne company
      const is403 = error.response?.status === 403;
      if (is403 && companyId) {
        clearSelectedCompany();
        try {
          const data = await fetchAndApplySession(set, null);

          if (get()._initSeq !== seq) return;

          if (data.activeCompany?._id) {
            setSelectedCompany(data.activeCompany._id, {
              name: data.activeCompany.name,
              slug: data.activeCompany.slug,
            });
          }
          return;
        } catch (retryError) {
          if (get()._initSeq !== seq) return;
          set(describeFailure(retryError));
          return;
        }
      }

      set(describeFailure(error));
    }
  },

  /**
   * Change de company active
   *
   * Utilise un compteur de sequence pour ignorer les reponses obsoletes
   * en cas de switchs rapides successifs.
   *
   * 1. Appel API switch
   * 2. Invalidation caches SWR business de l'ancienne company
   * 3. Rechargement session pour la nouvelle company
   *
   * @param {string} companyId - ID de la nouvelle company
   */
  switchCompany: async (companyId) => {
    const previousCompanyId = get().activeCompany?._id;
    const seq = get()._switchSeq + 1;
    set({status: SESSION_STATUS.LOADING, _switchSeq: seq});

    try {
      // Appel API pour mettre a jour activeCompanyId en BDD
      await axiosInstance.post(endpoints.auth.companies.switch(companyId));

      // Ignorer si un switch plus recent a ete lance entre-temps
      if (get()._switchSeq !== seq) return;

      // Invalider les caches SWR business de l'ancienne company
      if (previousCompanyId) {
        await mutate(
            (key) => {
              if (typeof key !== 'string') return false;
              return key.includes('/app/') && key.includes(previousCompanyId);
            },
            undefined,
            {revalidate: false},
        );
      }

      // Ignorer si un switch plus recent a ete lance entre-temps
      if (get()._switchSeq !== seq) return;

      // Recharger la session pour la nouvelle company
      await get().initialize(companyId);

      if (get()._switchSeq !== seq) return;

      // `initialize` n'échoue plus par exception : il inscrit l'issue dans
      // l'état. La bascule ne vaut donc que si la session est réellement
      // chargée, sinon l'appelant annoncerait « établissement changé » et
      // redirigerait par-dessus un écran de reconnexion.
      const {status, error: sessionError} = get();
      if (status !== SESSION_STATUS.READY) {
        throw new Error(sessionError || 'Session indisponible après le changement d\'établissement');
      }
    } catch (error) {
      // Ne pas ecraser l'etat si un switch plus recent est en cours
      if (get()._switchSeq !== seq) return;

      // Une session momentanément indisponible le reste : le provider continue
      // de retenter seul. La basculer en 'error' remplacerait cette reprise
      // automatique par un bouton à cliquer, au milieu du service.
      if (get().status !== SESSION_STATUS.UNAVAILABLE) {
        set({status: SESSION_STATUS.ERROR, error: error.message || 'Erreur de switch company'});
      }

      throw error;
    }
  },

  /**
   * Rafraichit la session silencieusement (sans passer par loading)
   *
   * Utilise pour les events discrets (permissions modifiees, subscription changee)
   * et le refresh au focus de fenetre quand les donnees sont stale.
   * En cas d'echec, stocke l'erreur sans changer le status pour permettre
   * au SessionProvider de detecter les erreurs persistantes.
   */
  refreshSession: async () => {
    try {
      await fetchAndApplySession(set, get().activeCompany?._id);
    } catch {
      // Echec silencieux : on conserve les donnees existantes en memoire
      // pour ne pas interrompre l'utilisateur sur un blip reseau transitoire
    }
  },

  /**
   * Reset complet du store (logout)
   */
  reset: () => {
    set(INITIAL_STATE);
  },

  /**
   * Marque la session comme terminée.
   *
   * Statut terminal : plus aucun chargement ne doit être tenté, une page de
   * connexion est en route.
   */
  close: () => {
    set({...INITIAL_STATE, status: SESSION_STATUS.CLOSED});
  },
}));

export default useSessionStore;
